import type { RagSourceType } from '../types';

export type RagSourceLinkContext = {
  companyUpdateCompanyId?: (updateId: string) => string | undefined;
  monthlyUpdateCompanyId?: (updateId: string) => string | undefined;
  portfolioRadarRoute?: (signalId: string) => string | undefined;
};

/** Citation source kinds that may appear before shared types catch up. */
export type RagCitationSourceType = RagSourceType | 'portfolio_radar';

const SOURCE_TYPE_LABELS: Record<RagCitationSourceType, string> = {
  financial_item: 'Asset registry',
  investment: 'Portfolio company',
  bank_account: 'Bank account',
  bank_transaction: 'Bank transaction',
  company_update: 'Company update',
  monthly_update: 'Monthly report',
  investment_case: 'Investment case',
  real_estate: 'Real estate',
  portfolio_radar: 'Portfolio radar',
  business_opportunity: 'Business opportunity',
};

function companyDetailPath(companyId: string, tab?: 'details' | 'reports' | 'notes' | 'documents'): string {
  const encoded = encodeURIComponent(companyId);
  if (!tab || tab === 'details') {
    return `/investments/${encoded}`;
  }
  return `/investments/${encoded}?tab=${tab}`;
}

export function ragSourceTypeLabel(sourceType: RagCitationSourceType): string {
  return SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}

export function resolveRagSourcePath(
  sourceType: RagCitationSourceType,
  sourceId: string,
  context: RagSourceLinkContext = {}
): string | null {
  const id = sourceId.trim();
  if (!id) {
    return null;
  }

  switch (sourceType) {
    case 'financial_item':
      return `/assets?item=${encodeURIComponent(id)}&tab=documents`;
    case 'investment':
      return companyDetailPath(id, 'documents');
    case 'bank_account':
    case 'bank_transaction':
      return `/bank-accounts/${encodeURIComponent(id)}`;
    case 'investment_case':
      return `/investment-cases/${encodeURIComponent(id)}`;
    case 'business_opportunity':
      return `/opportunities/${encodeURIComponent(id)}`;
    case 'real_estate':
      return `/real-estate/${encodeURIComponent(id)}?tab=documents`;
    case 'company_update': {
      const companyId = context.companyUpdateCompanyId?.(id);
      return companyId ? companyDetailPath(companyId, 'notes') : null;
    }
    case 'monthly_update': {
      const companyId = context.monthlyUpdateCompanyId?.(id);
      return companyId ? companyDetailPath(companyId, 'reports') : null;
    }
    case 'portfolio_radar':
      return context.portfolioRadarRoute?.(id) ?? null;
    default:
      return null;
  }
}
